'use client';

import {
  useScroll,
  useTransform,
  useInView,
  motion,
  AnimatePresence,
} from 'framer-motion';
import React, {
  FC, memo, useEffect, useRef, useState,
} from 'react';
import { cn } from '@/lib/utils';

export interface TimelineEntry {
  title: string;
  content: React.ReactNode;
}

const TimelineMarker: FC<{ label: string }> = ({ label }) => {
  const markerRef = useRef<HTMLDivElement>(null);
  // The marker is `sticky top-40` (160px). The active band must START at the pin line —
  // a centred -45%/-45% band is never intersected while the marker is pinned.
  const isActive = useInView(markerRef, { margin: '-150px 0px -70% 0px' });

  return (
    <div
      ref={markerRef}
      className="sticky flex flex-col md:flex-row z-40 items-center top-40 self-start max-w-xs lg:max-w-sm md:w-full"
    >
      <div className="h-10 absolute left-3 md:left-3 w-10 rounded-full bg-white dark:bg-black flex items-center justify-center">
        <motion.div
          animate={{
            backgroundColor: isActive ? '#cbacf9' : 'rgb(38 38 38)',
            borderColor: isActive ? 'rgba(203,172,249,0.55)' : 'rgb(64 64 64)',
            boxShadow: isActive ? '0 0 0 5px rgba(203,172,249,0.14)' : '0 0 0 0px rgba(203,172,249,0)',
            scale: isActive ? 1.15 : 1,
          }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="h-4 w-4 rounded-full border p-2"
        />
      </div>
      <h3 className={cn('hidden md:block text-xl md:pl-20 md:text-2xl font-bold transition-colors duration-300', isActive ? 'text-white' : 'text-neutral-500')}>
        {label}
      </h3>
    </div>
  );
};

export const Timeline = memo(({ data }: { data: TimelineEntry[] }) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const updateHeight = () => {
      const rect = el.getBoundingClientRect();
      setHeight(rect.height);
    };

    updateHeight();

    const ro = new ResizeObserver(() => updateHeight());
    ro.observe(el);

    window.addEventListener('resize', updateHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [data]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 10%', 'end 100%'],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div
      className="w-full md:px-10 rounded-3xl border border-white/[0.1] transition-colors duration-300 hover:border-white/[0.18]"
      ref={containerRef}
      style={{ background: 'rgb(4,7,29)', backgroundImage: 'linear-gradient(90deg, rgba(4,7,29,1) 0%, rgba(12,14,35,1) 100%)' }}
    >
      <div ref={ref} className="relative max-w-7xl mx-auto pb-20">
        <AnimatePresence initial={false}>
          {data.map((item, index) => (
            <motion.div
              key={`${item.title}-${index}`}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="flex justify-start pt-10 md:pt-40 md:gap-10"
            >
              <TimelineMarker label={item.title} />

              <div className="relative pl-20 pr-4 md:pl-4 w-full">
                <h3 className="md:hidden block text-2xl mb-4 text-left font-bold text-neutral-500 dark:text-neutral-500">
                  {item.title}
                </h3>
                {item.content}
                {' '}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <div
          style={{
            height: `${height}px`,
          }}
          className="absolute md:left-8 left-8 top-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-[0%] via-neutral-200 dark:via-neutral-700 to-transparent to-[99%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)] "
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0  w-[2px] bg-gradient-to-t from-purple via-blue-500 to-transparent from-[0%] via-[10%] rounded-full"
          />
        </div>
      </div>
    </div>
  );
});

Timeline.displayName = 'Timeline';
